import React, { useMemo } from 'react'
import { Autocomplete, TextField, Box, Typography, Paper } from '@mui/material'
import { TranslationMapContentType } from 'common/types/kraken-core/TranslationMap'
import { useSearchContentTypes } from 'pages/TranslationMaps/api'
import { Z_INDEX } from 'common/constants'

export interface MUIContentTypeSelectorProps {
  selected?: {
    label?: string
    value?: string
  }
  onSelect: (selected: TranslationMapContentType | null) => void
  disabled?: boolean
  placeholder?: string
  label?: string
  required?: boolean
  error?: boolean
  helperText?: string
}

export default function MUIContentTypeSelector({
  selected,
  onSelect,
  disabled = false,
  placeholder = 'Select a Content Type',
  label = 'Content Type',
  required = false,
  error = false,
  helperText,
}: MUIContentTypeSelectorProps) {
  const { data: contentTypes, isLoading } = useSearchContentTypes()

  // Memoize the selected value
  const selectedValue = useMemo(() => {
    if (selected?.value && selected?.label) {
      return contentTypes?.find(ct => ct.content_type === selected.value) || null
    }
    return null
  }, [selected?.value, selected?.label, contentTypes])

  const handleChange = (event: any, newValue: TranslationMapContentType | null) => {
    onSelect(newValue)
  }

  return (
    <Autocomplete
      value={selectedValue}
      onChange={(event, newValue) => onSelect(newValue as TranslationMapContentType | null)}
      options={contentTypes || []}
      getOptionLabel={option => {
        if (typeof option === 'string') return option
        return option.description || option.content_type
      }}
      isOptionEqualToValue={(option, value) => option.content_type === value?.content_type}
      loading={isLoading}
      disabled={disabled || isLoading}
      multiple={false}
      renderInput={params => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {option.description || option.content_type}
            </Typography>
            {option.content_type &&
              option.description &&
              option.content_type !== option.description && (
                <Typography variant="caption" color="text.secondary">
                  Type: {option.content_type}
                </Typography>
              )}
          </Box>
        </Box>
      )}
      noOptionsText={isLoading ? 'Loading...' : 'No content types found'}
      loadingText="Loading content types..."
      sx={{
        '& .MuiAutocomplete-popper': {
          zIndex: Z_INDEX.SELECT_MENU,
        },
      }}
      PaperComponent={({ children, ...other }) => (
        <Paper
          {...other}
          sx={{
            zIndex: Z_INDEX.SELECT_MENU,
            '& .MuiAutocomplete-listbox': {
              maxHeight: 300,
            },
            boxShadow: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {children}
        </Paper>
      )}
    />
  )
}
